import * as React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import { useAppDispatch, useAppSelector } from '../appHooks';
import { RootState } from '../store';
import { useGetReleaseNotesQuery } from '../redux/appSettingsAPI';
import { closeReleaseNotes, openReleaseNotes } from '../redux/releaseNotesVisibility';
import LogToDB from '../types/dbLogs';
import '../styles/ReleaseNotesSnackbar.scss';

const markdownComponents = {
    a: (props: any) => <a target="_blank" rel="noopener noreferrer" {...props} />,
};

const ReleaseNotesSnackbar: React.FC = () => {
    const dispatch = useAppDispatch();
    const { isOpen, lastSeenVersion } = useAppSelector((state: RootState) => state.persistedReleaseNotesVisibilityReducer);
    const { data: releaseNotes } = useGetReleaseNotesQuery();

    const [index, setIndex] = React.useState(1);
    const wasOpen = React.useRef(false);

    const notes = React.useMemo(() => releaseNotes ?? [], [releaseNotes]);
    const latestVersion = notes.length > 0 ? notes[0].version : '';

    // A newly published version always re-opens the snack, whatever the persisted state.
    React.useEffect(() => {
        if (latestVersion !== '' && latestVersion !== lastSeenVersion) {
            setIndex(0);
            dispatch(openReleaseNotes());
        }
    }, [latestVersion, lastSeenVersion, dispatch]);

    React.useEffect(() => {
        // Wait for the notes to be loaded so the logged version is meaningful.
        if (latestVersion === '') {
            return;
        }

        if (isOpen && !wasOpen.current) {
            void LogToDB('ReleaseNotes', latestVersion);
        }

        wasOpen.current = isOpen;
    }, [isOpen, latestVersion]);

    if (!isOpen || notes.length === 0) {
        return null;
    }

    const current = notes[Math.min(index, notes.length - 1)];

    return (
        <Snackbar
            open
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{ maxWidth: 480 }}
        >
            <Paper elevation={8} sx={{ width: 480, maxWidth: '100%', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, bgcolor: 'secondary.main', color: 'secondary.contrastText' }}>
                    <Tooltip title="Newer version">
                        <span>
                            <IconButton
                                size="small"
                                color="inherit"
                                disabled={index === 0}
                                onClick={() => setIndex((i) => i - 1)}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography sx={{ flex: 1, textAlign: 'center', fontSize: '0.8rem', fontWeight: 600 }}>
                        {`What's new - v${current.version}`}
                    </Typography>
                    <Tooltip title="Previous version">
                        <span>
                            <IconButton
                                size="small"
                                color="inherit"
                                disabled={index >= notes.length - 1}
                                onClick={() => setIndex((i) => i + 1)}
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: 'rgba(255,255,255,0.4)' }} />
                    <Tooltip title="Close">
                        <IconButton size="small" color="inherit" onClick={() => dispatch(closeReleaseNotes(latestVersion))}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box
                    className="release-notes-markdown"
                    sx={(theme) => ({
                        px: 2,
                        py: 1,
                        maxHeight: 360,
                        overflowY: 'auto',
                        '--rn-text-color': theme.palette.text.primary,
                        '--rn-heading-color': theme.palette.secondary.main,
                        '--rn-subheading-color': theme.palette.text.secondary,
                        '--rn-strong-color': theme.palette.primary.main,
                        '--rn-link-color': theme.palette.primary.main,
                        '--rn-divider-color': theme.palette.divider,
                        '--rn-code-bg': theme.palette.action.hover,
                    })}
                >
                    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {current.content}
                    </Markdown>
                </Box>
            </Paper>
        </Snackbar>
    );
};

export default ReleaseNotesSnackbar;
